/**
 * API client do motor de preco DIVISOR (ADR-0050 — spec orcamento §0.9).
 *
 * Endpoints (sem trailing slash, padrao do projeto):
 *   GET    /api/orcamentos/{id}/preco            payload derivado (shape unico)
 *   PATCH  /api/orcamentos/{id}/preco-final      {preco_final_definido, updated_at,
 *                                                 justificativa?} — 409 = conflito
 *                                                 de concorrencia (recarregue)
 *   CRUD   /api/orcamento-areas?ambiente=        nivel M2 (AreaAmbiente)
 *   CRUD   /api/orcamento-insumos?ambiente=      ferragens/fita/revenda (InsumoAmbiente)
 *   GET    /api/financeiro/sugestao-k?janela_meses=3&unidade=
 *
 * O payload de /preco NUNCA e persistido no frontend: piso, minimo, numerador,
 * divisor e throughput sao recalculados pelo backend a cada leitura.
 */

const PREFIXO_ORC = "/api/orcamentos";
const PREFIXO_AREA = "/api/orcamento-areas";
const PREFIXO_INSUMO = "/api/orcamento-insumos";

// ---------- Preco (payload derivado + preco final) ----------

export function getPrecoOrcamento(request, orcamentoId, options = {}) {
  return request(`${PREFIXO_ORC}/${orcamentoId}/preco`, options);
}

/**
 * Define o preco final digitado pelo vendedor.
 *
 * Concorrencia otimista (R17): envia o `updated_at` que a tela leu do
 * orcamento; o backend responde 409 quando outra pessoa alterou no meio —
 * o caller mostra o banner "recarregue" e NAO tenta de novo sozinho.
 * Alcadas (G14/G15): faixa amarela exige justificativa; vermelha exige
 * justificativa >= 20 chars + MFA recente (o interceptor global de MFA
 * do AuthContext cuida do step-up quando o backend pedir).
 */
export function patchPrecoFinal(
  request,
  orcamentoId,
  { preco_final_definido, updated_at, justificativa },
  options = {},
) {
  const body = { preco_final_definido, updated_at };
  if (justificativa !== undefined && justificativa !== "") {
    body.justificativa = justificativa;
  }
  return request(`${PREFIXO_ORC}/${orcamentoId}/preco-final`, {
    method: "PATCH",
    body: JSON.stringify(body),
    ...options,
  });
}

/**
 * Escolhe/troca (ou limpa, com `condicaoId = null`) a forma de pagamento do
 * orcamento DIVISOR (Tarefa F2). O backend snapshota a taxa (s2) na
 * PrecificacaoOrcamento, re-aloca os precos e devolve o payload de /preco
 * atualizado. O caller dispara "orc2:preco-invalidado" apos o sucesso para
 * o ComposicaoPrecoDivisor recarregar sozinho.
 */
export function patchCondicaoPagamento(request, orcamentoId, condicaoId, options = {}) {
  return request(`${PREFIXO_ORC}/${orcamentoId}/condicao-pagamento`, {
    method: "PATCH",
    body: JSON.stringify({ condicao_pagamento: condicaoId || null }),
    ...options,
  });
}

// ---------- AreaAmbiente (nivel M2 — N linhas por ambiente) ----------

export function listarAreasAmbiente(request, ambienteId, options = {}) {
  return request(`${PREFIXO_AREA}?ambiente=${ambienteId}`, options);
}

export function criarAreaAmbiente(request, dados) {
  // dados: {ambiente, produto, m2_informados, item?, ordem?}
  return request(PREFIXO_AREA, {
    method: "POST",
    body: JSON.stringify(dados),
  });
}

export function atualizarAreaAmbiente(request, id, dados) {
  return request(`${PREFIXO_AREA}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(dados),
  });
}

export function removerAreaAmbiente(request, id) {
  return request(`${PREFIXO_AREA}/${id}`, { method: "DELETE" });
}

// ---------- InsumoAmbiente (ferragem/acessorio/fita/revenda — 3 niveis) ----------

export function listarInsumosAmbiente(request, ambienteId, options = {}) {
  return request(`${PREFIXO_INSUMO}?ambiente=${ambienteId}`, options);
}

export function criarInsumoAmbiente(request, dados) {
  // dados: {ambiente, produto, quantidade, item?, observacao?, ordem?}
  return request(PREFIXO_INSUMO, {
    method: "POST",
    body: JSON.stringify(dados),
  });
}

export function atualizarInsumoAmbiente(request, id, dados) {
  return request(`${PREFIXO_INSUMO}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(dados),
  });
}

export function removerInsumoAmbiente(request, id) {
  return request(`${PREFIXO_INSUMO}/${id}`, { method: "DELETE" });
}

// ---------- Sugestao de k (financeiro — selo "sugerido pelo financeiro") ----------

/**
 * k = OE / TVC dos ultimos N meses-calendario FECHADOS (default 3).
 * Permissao: financeiro:view — callers sem ela devem passar
 * `{ silentError: true }` e tratar a ausencia como "sem selo".
 * Retorna {k_sugerido_percentual|null, oe, tvc, periodo_inicio, periodo_fim,
 *          janela_meses, percentual_em_outros, motivo}.
 */
export function getSugestaoK(request, { janelaMeses = 3, unidade } = {}, options = {}) {
  const usp = new URLSearchParams();
  usp.set("janela_meses", String(janelaMeses));
  if (unidade) usp.set("unidade", unidade);
  return request(`/api/financeiro/sugestao-k?${usp.toString()}`, options);
}
